import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import {
  Button,
  Chip,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { green, red } from "@mui/material/colors";
import React, { useContext, useState } from "react";
import { PatientContext } from "../../contexts/PatientContext.tsx";
import { ProjectContext } from "../../contexts/ProjectContext";
import CloseArrowTooltip from "../CloseArrowTooltip";
import PatientActivity from "../activity/PatientActivity";
import MarkModal from "./MarkModal";
import VisitsDrawer from "./VisitsDrawer";
import ImdrfCategorize from "./categorize/IMDRFCategorize";
import SelectedPatientSkeleton from "./skeletons/SelectedPatientSkeleton";

const openEnum = {
  categorizeModal: "categorize-modal",
  markModal: "mark-modal",
  patientActivity: "patient-activity",
  patientVisitDrawer: "display-visit-drawer",
};

export default function SelectedPatientMetadata({
  patientMaxMode,
  setPatientMaxMode,
  prevDisabled,
  goPrevPatient,
  nextDisabled,
  goNextPatient,
}: {
  patientMaxMode: boolean;
  setPatientMaxMode: (newMode: boolean) => void;
  prevDisabled: boolean;
  goPrevPatient: () => void;
  nextDisabled: boolean;
  goNextPatient: () => void;
}) {
  const patient = useContext(PatientContext);
  const { isLocked } = useContext(ProjectContext)!;
  const [open, setOpen] = useState<string | null>(null);
  const [markButton, setMarkButton] = useState<Element | null>(null);
  const [visitToLoadId, setVisitToLoadId] = useState<string | null>(null);

  function handleCategorizeModal(event: React.MouseEvent<HTMLButtonElement>) {
    setOpen(openEnum.categorizeModal);
    setMarkButton(event.currentTarget);
  }

  function handleMarkModal(event: React.MouseEvent<HTMLButtonElement>) {
    setOpen(openEnum.markModal);
    setMarkButton(event.currentTarget);
  }

  function handlePatientActivity() {
    setOpen(openEnum.patientActivity);
  }

  function handleOnClose() {
    setOpen(null);
    setMarkButton(null);
  }

  function onLoadVisit(visitId: string) {
    setVisitToLoadId(visitId);
    setOpen(openEnum.patientVisitDrawer);
  }

  if (!patient || !patient.patientNumber) {
    return <SelectedPatientSkeleton />;
  }

  return (
    <Paper className="patient-header" elevation={0} square>
      <Stack direction={"row"} justifyContent={"space-between"}>
        <Stack
          direction={"row"}
          spacing={1}
          alignItems={"center"}
          className={"patient-header-left"}
        >
          <IconButton onClick={() => setPatientMaxMode(!patientMaxMode)}>
            {!patientMaxMode ? <OpenInFullIcon /> : <CloseFullscreenIcon />}
          </IconButton>
          <Divider orientation="vertical" flexItem />
          <Stack direction={"row"} alignItems={"center"}>
            <IconButton
              onClick={goPrevPatient}
              sx={{ "&:hover": { bgcolor: "action.hover" } }}
              disabled={prevDisabled}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant={"h6"}>
              {`Patient ${patient.patientNumber}`}
            </Typography>
            <IconButton
              onClick={goNextPatient}
              sx={{ "&:hover": { bgcolor: "action.hover" } }}
              disabled={nextDisabled}
            >
              <ArrowForwardIcon />
            </IconButton>
          </Stack>
          <Divider orientation="vertical" flexItem />
          {patient.isNew && (
            <Chip
              label={"New"}
              size="small"
              sx={{ backgroundColor: "info.main", color: "common.white" }}
            ></Chip>
          )}
          {patient.isIncluded && (
            <Chip
              label={"Included"}
              size="small"
              sx={{ bgcolor: green[600], color: "common.white" }}
              onDelete={() => undefined}
              deleteIcon={
                <Tooltip className="reason-icon" title={patient.reason}>
                  <StickyNote2OutlinedIcon
                    fontSize="small"
                    style={{ color: "#F0F0F0" }}
                  />
                </Tooltip>
              }
            ></Chip>
          )}
          {patient.isIncluded === false && (
            <Chip
              label={"Excluded"}
              size="small"
              sx={{ bgcolor: red[600], color: "common.white" }}
              onDelete={() => undefined}
              deleteIcon={
                <Tooltip className="reason-icon" title={patient.reason}>
                  <StickyNote2OutlinedIcon
                    fontSize="small"
                    style={{ color: "#F0F0F0" }}
                  />
                </Tooltip>
              }
            ></Chip>
          )}
        </Stack>
        <Stack direction={"row"} className={"patient-header-right"}>
          <Button
            size={"small"}
            variant={"outlined"}
            className={"categorize-button"}
            onClick={handlePatientActivity}
          >
            View Patient Activity
          </Button>
          <CloseArrowTooltip title={isLocked ? "This project is locked." : ""}>
            <span>
              <Button
                size={"small"}
                variant={"outlined"}
                className={"categorize-button"}
                onClick={handleCategorizeModal}
                disabled={isLocked}
              >
                Categorize Patient
              </Button>
            </span>
          </CloseArrowTooltip>
          <CloseArrowTooltip title={isLocked ? "This project is locked." : ""}>
            <span>
              <Button
                size={"small"}
                variant="contained"
                className={"categorize-button"}
                onClick={handleMarkModal}
                disabled={isLocked}
              >
                Mark Patient As
              </Button>
            </span>
          </CloseArrowTooltip>
        </Stack>
        {open === openEnum.categorizeModal && (
          <ImdrfCategorize
            onClose={handleOnClose}
            anchorVertical={"bottom"}
            open={true}
            anchorEl={markButton}
          />
        )}
        {open === openEnum.markModal && (
          <MarkModal
            onClose={handleOnClose}
            open={true}
            anchorEl={markButton}
          />
        )}
        {open === openEnum.patientActivity && (
          <PatientActivity onClose={handleOnClose} onLoadVisit={onLoadVisit} />
        )}
        {open === openEnum.patientVisitDrawer && visitToLoadId && (
          <VisitsDrawer
            toggleDrawer={handleOnClose}
            initialVisitId={visitToLoadId}
          />
        )}
      </Stack>
    </Paper>
  );
}
